import { useState } from "react";
import { Briefcase, Check, Pencil, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BOS_ACTIONS, bosActionLabel } from "@/lib/bosActions";
import BosPipeBlock from "./BosPipeBlock";
import { JalonsAvalBlock } from "./JalonActions";

/**
 * V4.28 — Bloc Dossier délégué au BOS dans la fiche dossier.
 *
 * - Toggle activer/désactiver
 * - Edition du nom du gestionnaire BOS
 * - Affiche : date de délégation, dernière relance, prochaine relance (lundi suivant)
 * - Bouton "BOS relancé" pour clôturer le contrôle hebdo
 */

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR");
  } catch { return iso; }
}

export default function BosBlock({ dossier, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(dossier.bos_name || "");
  const [saving, setSaving] = useState(false);

  const isDelegated = !!dossier.bos_delegated;

  const toggleDelegation = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/dossiers/${dossier.id}/bos`, {
        delegated: !isDelegated,
        bos_name: name || dossier.bos_name || "",
      });
      onUpdated(res.data);
      toast.success(!isDelegated ? "Dossier délégué au BOS" : "Délégation BOS désactivée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const saveName = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/dossiers/${dossier.id}/bos`, {
        delegated: true,
        bos_name: name,
      });
      onUpdated(res.data);
      setEditing(false);
      toast.success("Gestionnaire BOS mis à jour");
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const markFollowup = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/dossiers/${dossier.id}/bos/followup`);
      onUpdated(res.data);
      toast.success("BOS relancé — prochaine vérif lundi prochain");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  // V4.29 — Action attendue du BOS
  const updateAction = async (next_action, next_action_custom = "") => {
    setSaving(true);
    try {
      const res = await api.post(`/dossiers/${dossier.id}/bos`, {
        delegated: true,
        bos_next_action: next_action,
        bos_next_action_custom: next_action === "autre" ? next_action_custom : "",
      });
      onUpdated(res.data);
    } catch (e) {
      toast.error("Erreur — action non sauvegardée");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card-base p-4 mb-4" data-testid="bos-block">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Briefcase size={18} weight="fill" color="var(--cf-accent)" />
          <h3 className="font-display text-base font-extrabold text-slate-900">
            Dossier délégué au BOS
          </h3>
          <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid="bos-eligible-badge" title="RDV bancaire planifié ou étape aval atteinte : le dossier peut entrer dans le pipe BOS">
            Éligible BOS
          </span>
          {isDelegated && (
            <span
              className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded bg-violet-100 text-violet-800"
              data-testid="bos-active-badge"
            >
              Actif
            </span>
          )}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="bos-toggle">
          <span className="text-[12px] font-bold text-slate-600">
            {isDelegated ? "Désactiver" : "Activer"}
          </span>
          <input
            type="checkbox"
            checked={isDelegated}
            disabled={saving}
            onChange={toggleDelegation}
            className="w-10 h-5 rounded-full appearance-none bg-slate-300 checked:bg-violet-600 relative cursor-pointer transition-colors before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 checked:before:left-[1.375rem] before:transition-all"
          />
        </label>
      </div>

      {/* Pipe BOS — état réel, action, relances tracées (visible dès éligibilité, indépendant de la délégation) */}
      <BosPipeBlock dossier={dossier} onUpdated={onUpdated} />
      <JalonsAvalBlock dossier={dossier} onUpdated={onUpdated} />

      {!isDelegated ? (
        <p className="text-[12.5px] text-slate-500 leading-snug">
          Active la délégation BOS pour confier le suivi opérationnel (banque, offre, notaire) à ton service back-office. Tu n&apos;auras plus qu&apos;à contrôler une fois par semaine.
        </p>
      ) : (
        <div className="space-y-2 text-[12.5px]">
          {/* Gestionnaire BOS */}
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold min-w-[120px]">Gestionnaire :</span>
            {editing ? (
              <div className="flex-1 flex items-center gap-1">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nom du gestionnaire BOS"
                  className="flex-1 px-2 py-1 rounded border border-slate-200 text-[12.5px]"
                  autoFocus
                  data-testid="bos-name-input"
                />
                <button onClick={saveName} disabled={saving} className="p-1 text-emerald-700 hover:bg-emerald-50 rounded" data-testid="bos-name-save">
                  <Check size={14} weight="bold" />
                </button>
                <button onClick={() => { setEditing(false); setName(dossier.bos_name || ""); }} className="p-1 text-slate-500 hover:bg-slate-50 rounded">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="flex-1 flex items-center gap-2">
                <span className="font-bold text-slate-800">{dossier.bos_name || "—"}</span>
                <button onClick={() => setEditing(true)} className="p-1 text-slate-400 hover:text-slate-700" data-testid="bos-name-edit">
                  <Pencil size={12} weight="bold" />
                </button>
              </div>
            )}
          </div>

          {/* Date délégation */}
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold min-w-[120px]">Délégué le :</span>
            <span className="font-bold text-slate-800">{fmtDate(dossier.bos_delegated_at) || "—"}</span>
          </div>

          {/* Dernière relance */}
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold min-w-[120px]">Dernière relance :</span>
            <span className="font-bold text-slate-800">{fmtDate(dossier.bos_last_followup_date) || "—"}</span>
          </div>

          {/* Prochaine relance */}
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold min-w-[120px]">Prochain contrôle :</span>
            <span className="font-bold text-violet-700" data-testid="bos-next-followup">
              {fmtDate(dossier.bos_next_followup_date) || "—"}
            </span>
          </div>

          {/* V4.29 — Action attendue */}
          <div className="flex items-start gap-2 pt-1">
            <span className="text-slate-500 font-semibold min-w-[120px] pt-1.5">Prochaine action :</span>
            <div className="flex-1 space-y-1.5">
              <select
                value={dossier.bos_next_action || ""}
                onChange={(e) => updateAction(e.target.value, dossier.bos_next_action_custom || "")}
                disabled={saving}
                className="w-full px-2 py-1.5 rounded border border-slate-200 text-[12.5px] font-semibold text-slate-800 bg-white"
                data-testid="bos-next-action-select"
              >
                <option value="">— Choisir —</option>
                {BOS_ACTIONS.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
                {/* V4.37 — Affiche la valeur legacy (wait_accord_banque, wait_offre, wait_appel_fonds)
                    en option grisée pour que le select ne soit pas vide sur les dossiers historiques.
                    Au prochain changement, la valeur sera remplacée par une option à jour. */}
                {dossier.bos_next_action &&
                  !BOS_ACTIONS.some((a) => a.key === dossier.bos_next_action) && (
                    <option value={dossier.bos_next_action} disabled>
                      {bosActionLabel(dossier.bos_next_action)} — à mettre à jour
                    </option>
                  )}
              </select>
              {dossier.bos_next_action === "autre" && (
                <input
                  type="text"
                  defaultValue={dossier.bos_next_action_custom || ""}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (dossier.bos_next_action_custom || "")) {
                      updateAction("autre", v);
                    }
                  }}
                  placeholder="Préciser l'action attendue…"
                  className="w-full px-2 py-1.5 rounded border border-slate-200 text-[12.5px]"
                  data-testid="bos-next-action-custom"
                />
              )}
            </div>
          </div>

          <button
            onClick={markFollowup}
            disabled={saving}
            className="w-full mt-3 py-2 rounded-lg bg-violet-600 text-white font-extrabold text-[13px] hover:bg-violet-700 disabled:opacity-60 transition-colors"
            data-testid="bos-followup-btn"
          >
            BOS relancé
          </button>
        </div>
      )}
    </section>
  );
}
